import { DocumentTextIcon, FlagIcon, PencilAltIcon, TagIcon, TicketIcon, TrashIcon, UserIcon, ViewBoardsIcon, QuestionMarkCircleIcon } from "@heroicons/react/outline";
import { PencilIcon, UserCircleIcon } from "@heroicons/react/solid";
import { useDelete, useOne, useParse } from "@refinedev/core";
import { Breadcrumbs } from "components/breadcrumbs";
import Button from "components/button";
import { ConfirmModal } from "components/confirmModal";
import { DescriptionItem } from "components/descriptionItem";
import { FullScreenLoading } from "components/fullScreenLoading";
import { HistoryLogButton } from "components/historyLogButton";
import { IPickupAccountDetail, PickupAccountType } from "interfaces";
import { useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { isHaveActionPermission } from "utills/helpers";

const INITIAL_ACTION_MODAL_CONFIG = {
  open: false,
  submit: () => {}
}

export const PickupAccountDetail: React.FC = () => {
  const { mutate: deleteMutation, isLoading: deleteMutationIsLoading } = useDelete();
  const [confirmModalConfig, setConfirmModalConfig] = useState(INITIAL_ACTION_MODAL_CONFIG);

  const parse = useParse();
  const { params } = parse();
  const {
    data,
    isFetching,
    isPreviousData,
    isLoading,
    refetch: refetchDetail,
  } = useOne<IPickupAccountDetail>({
    resource: "pickup/accounts",
    id: params?.id,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!params?.id,
    } as any,
  });

  const _clearAccountToken = (id: string) => {
    deleteMutation(
      {
        resource: "pickup/accounts",
        id: `pos/${id}`,
        meta: {
          URLSuffix: "clear-token",
          ignoreResourceWrapper: true,
        },
        values: {},
      },
      {
        onSuccess: () => {
          toast.success("Cleared successfully");
          refetchDetail();
        },
      }
    );
  };

  const _onCloseConfirmModal = () => {
    setConfirmModalConfig(INITIAL_ACTION_MODAL_CONFIG);
  }
  const pickupAccountDetail = data?.data ?? null;

  if (isLoading) {
    return <FullScreenLoading />
  }

  return (
    <div className="w-full flex flex-col items-center gap-y-3 sm:gap-y-4">
      <Breadcrumbs
        customPageLabel="Pickup Account Detail"
        additionalLabel={
          <div className="flex gap-x-2.5">
            {(pickupAccountDetail?.role_name ?? "").toLowerCase() ===
              PickupAccountType.POS &&
            pickupAccountDetail?.logged_in &&
            isHaveActionPermission("accounts", "pickup", "edit") ? (
              <Button
                className="bg-brand-secondary-500 border-brand-secondary-500 border-[1px] border-solid text-white hover:bg-brand-secondary-500 active:bg-brand-secondary-500 active:opacity-85"
                onClick={() =>
                  setConfirmModalConfig({
                    open: true,
                    submit: () =>
                      _clearAccountToken(pickupAccountDetail?.id ?? ""),
                  })
                }
              >
                <span className="sr-only">Clear Token</span>
                <TrashIcon className="size-4 text-white" />{" "}
                <span className="hidden sm:block sm:ml-2">Clear Token</span>
              </Button>
            ) : (
              <></>
            )}
            <div>
              <HistoryLogButton
                resource={`pickup/accounts/${pickupAccountDetail?.id}/history`}
                customFilters={[
                  {
                    field: "role_name",
                    operator: "eq",
                    value: pickupAccountDetail?.role_name.toLowerCase(),
                  },
                ]}
                className="sm:h-auto"
              />
            </div>
            {isHaveActionPermission("accounts", "pickup", "edit") ? (
              <Link to={`/pickup/accounts/${pickupAccountDetail?.id}/edit`}>
                <Button className="h-10 w-10 sm:h-auto sm:w-auto">
                  <span className="sr-only">Edit</span>
                  <PencilIcon className="w-4 h-4 text-white" />{" "}
                  <span className="hidden sm:block sm:ml-2">Edit</span>
                </Button>
              </Link>
            ) : (
              <></>
            )}
          </div>
        }
      />

      <div className="box-container p-0">
        <div className="divide-y divide-gray-100">
          <DescriptionItem
            icon={TagIcon}
            label="Status"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {pickupAccountDetail?.is_active ? "Active" : "Inactive"}
              </p>
            }
          />
          
          {pickupAccountDetail?.role_name?.toLowerCase() === PickupAccountType?.POS?.toLowerCase() && (
            <DescriptionItem
              icon={QuestionMarkCircleIcon}
              label="Is Zone"
              content={
                <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                  {pickupAccountDetail?.is_zone ? "Yes" : "No"}
                </p>
              }
            />
          )}

          <DescriptionItem
            icon={PencilAltIcon}
            label="Update Permission"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {pickupAccountDetail?.is_editable ? "Updatable": "Non-updatable"}
              </p>
            }
          />
          <DescriptionItem
            icon={TicketIcon}
            label="Event name"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {pickupAccountDetail?.event_name}
              </p>
            }
          />
          <DescriptionItem
            icon={UserIcon}
            label="Account name"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {pickupAccountDetail?.name}
              </p>
            }
          />
          <DescriptionItem
            icon={UserCircleIcon}
            label="User name"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {pickupAccountDetail?.username}
              </p>
            }
          />
          <DescriptionItem
            icon={DocumentTextIcon}
            label="Role name"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {pickupAccountDetail?.role_name}
              </p>
            }
          />
          <DescriptionItem
            icon={ViewBoardsIcon}
            label="Category"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {(pickupAccountDetail?.category ?? []).join(", ")}
              </p>
            }
          />
          <DescriptionItem
            icon={FlagIcon}
            label="Pickup Type"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4 capitalize">
                {pickupAccountDetail?.pickup_type}
              </p>
            }
          />
        </div>
      </div>
      {(isFetching && isPreviousData) || deleteMutationIsLoading ? <FullScreenLoading /> : <></>}
      <ConfirmModal 
        key="confirm-clear-token"
        title="Clear Token Confirmation"
        content="Are you sure you want to clear the token for this account?"
        open={confirmModalConfig.open}
        onConfirm={confirmModalConfig.submit}
        onClose={_onCloseConfirmModal}
      />
    </div>
  );
}